// Feature flags are set by the backend. Local overrides in localStorage are for testing only.

import { useQuery } from "@tanstack/react-query";

import { type RanaFeatureFlags, getRanaApiV1 } from "@nens/rana/api-v1";
import { useLocalStorage } from "@nens/react/hooks";

export type FeatureKey = keyof RanaFeatureFlags;

const getFeatureFlagsQuery = () =>
  getRanaApiV1().getQueryOptions({
    ignoreTenant: true,
    queryKey: ["feature-flags"],
    queryFn: () => getRanaApiV1(true).FrontendSettingsApi.getFrontendFeatureFlags(undefined),
  });

export function useFeatureFlagKeys(): FeatureKey[] {
  const { data: backendFeatureFlags } = useQuery(getFeatureFlagsQuery());
  return Object.keys(backendFeatureFlags ?? {}) as FeatureKey[];
}

export function useBackendFeatureFlag(feature: FeatureKey): boolean {
  const { data: backendFeatureFlags } = useQuery(getFeatureFlagsQuery());
  return backendFeatureFlags?.[feature] ?? false;
}

export function useIsFeatureEnabled(feature: FeatureKey): boolean {
  const [override] = useSetFeatureFlag(feature);
  const backendValue = useBackendFeatureFlag(feature);
  return override ?? backendValue;
}

export function useSetFeatureFlag(
  feature: FeatureKey
): [boolean | null, (enabled: boolean) => void] {
  const [featureFlags, setFeatureFlags] = useLocalStorage<Partial<Record<string, boolean>>>(
    "feature-flags",
    {}
  );

  const setSingleFeatureFlag = (enabled: boolean) => {
    setFeatureFlags({ ...featureFlags, [feature]: enabled });
  };
  const featureFlagState = featureFlags[feature] ?? null;

  return [featureFlagState, setSingleFeatureFlag];
}
